import { ChangeDetectionStrategy, Component, Input, ViewChild, forwardRef } from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { IonPopover } from "@ionic/angular";
import { IonicComponentsModule } from "src/components/ionic-components.module";
import { TranslatePipe, TranslateDirective } from "@ngx-translate/core";

/** One choice of {@link SelectFieldComponent} */
export interface SelectFieldOption<T = any> {
  value: T;
  label: string;
}

let nextSelectFieldId = 0;

/**
 * Drop-down field styled like the other inputs: the native <select> list is drawn by the OS and
 * cannot be themed, so the choices open in a popover as wide as the field. Works with
 * formControlName / ngModel.
 */
@Component({
  selector: "app-select-field",
  templateUrl: "./select-field.component.html",
  styleUrls: ["./select-field.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true,
  imports: [IonicComponentsModule, TranslatePipe, TranslateDirective],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectFieldComponent),
      multi: true,
    },
  ],
})
export class SelectFieldComponent implements ControlValueAccessor {
  @Input() options: Array<SelectFieldOption> = [];
  @Input() placeholder: string = "";
  /** Ionicons name shown at the start of the field */
  @Input() icon: string = "";
  /** id of the trigger button (lets a <label for> point at the field) */
  @Input() fieldId: string = `select-field-${nextSelectFieldId++}`;
  /** The option labels are translation keys */
  @Input() translateLabels: boolean = false;

  @ViewChild(IonPopover) private _popover: IonPopover;

  public value: any = null;
  public isDisabled: boolean = false;

  private _onChange: (value: any) => void = () => {};
  private _onTouched: () => void = () => {};

  /** Label of the current value, empty when nothing is selected */
  public get selectedLabel(): string {
    return this.options?.find((option) => option.value === this.value)?.label ?? "";
  }

  public onSelect(option: SelectFieldOption): void {
    this.value = option.value;
    this._onChange(option.value);
    this._onTouched();
    this._popover?.dismiss();
  }

  public onDismiss(): void {
    this._onTouched();
  }

  writeValue(value: any): void {
    this.value = value;
  }

  registerOnChange(fn: (value: any) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }
}
